import React, { useState, useCallback, useRef, useEffect } from 'react'
import styles from './LanguagePartnerView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import languages from '../languages.js'
import flags from '../flags.js'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'

const LANGUAGES = Object.entries(languages).map(([voice, label]) => ({
  voice,
  label,
  flag: flags[voice] ?? '🌐'
}))

const SCENARIOS = [
  { id: 'casual', label: 'Casual Chat', icon: '🗣' },
  { id: 'cafe', label: 'Coffee Shop', icon: '☕️' },
  { id: 'airport', label: 'Airport / Customs', icon: '🛂' },
  { id: 'directions', label: 'Asking Directions', icon: '🗺' },
  { id: 'hotel', label: 'Hotel Check-In', icon: '🏨' },
  { id: 'shopping', label: 'Shopping', icon: '🛒' },
  { id: 'custom', label: 'Custom Scenario...', icon: '✍️' }
]

const TranslatableWord = ({ word, fullMessage, serverUrl, sourceLang, apiKey }) => {
  const [translation, setTranslation] = useState(null)
  const [loading, setLoading] = useState(false)
  const hasFetched = useRef(false)

  const handleMouseEnter = async () => {
    // Clean word of punctuation for translation using a basic regex
    // We want to keep letters and numbers, strip punctuation
    const cleanWord = word.replace(/[^\p{L}\p{N}]+/gu, '').trim()
    if (!cleanWord || hasFetched.current) return

    hasFetched.current = true
    setLoading(true)

    try {
      const prompt = `Translate the word "${cleanWord}" from ${sourceLang.label} to English based on its context within the sentence: "${fullMessage}". Respond with ONLY the short English translation of that specific word. No quotes, no punctuation, no extra text.`
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify({
          model: 'qwen2.5:3b',
          prompt,
          stream: false,
          options: { temperature: 0.1, num_predict: 10 }
        })
      })
      console.log(cleanWord)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const resultText = data.response?.trim() || '?'
      setTranslation(resultText)
    } catch (err) {
      setTranslation('Error')
      hasFetched.current = false 
    }
    setLoading(false)
  }

  const isPunctuationOnly = word.replace(/[^\p{L}\p{N}]+/gu, '').trim() === ''
  if (isPunctuationOnly) {
    return <span>{word}</span>
  }

  return (
    <span className={styles.translatableWord} onMouseEnter={handleMouseEnter}>
      {word}
      <span className={styles.translationTooltip}>
        {loading ? (
          <div className={styles.tooltipDots}>
            <span />
            <span />
            <span />
          </div>
        ) : (
          translation || 'Hover to translate'
        )}
      </span>
    </span>
  )
}

export default function LanguagePartnerView({ voiceSpeed }) {
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState([]) // Chat history
  const [completions, setCompletions] = useState([])
  const [loadingCompletions, setLoadingCompletions] = useState(false)
  const [loadingChat, setLoadingChat] = useState(false)
  const [status, setStatusState] = useState({ type: 'idle', text: 'ready' })
  const [selectedLang, setSelectedLang] = useState(LANGUAGES[0])
  const [langOpen, setLangOpen] = useState(false)
  const [selectedScenario, setSelectedScenario] = useState(SCENARIOS[0])
  const [scenarioOpen, setScenarioOpen] = useState(false)
  const [customScenarioText, setCustomScenarioText] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [autoSpeak, setAutoSpeak] = useState(false)

  const inputRef = useRef(null)
  const debounceRef = useRef(null)
  const autocompleteControllerRef = useRef(null)
  const chatControllerRef = useRef(null)
  const chatLogRef = useRef(null)
  const langRef = useRef(null)
  const scenarioRef = useRef(null)
  const recognitionRef = useRef(null)
  const messagesRef = useRef([])
  const autoSpeakRef = useRef(autoSpeak)

  useEffect(() => {
    autoSpeakRef.current = autoSpeak
  }, [autoSpeak])

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = `${Math.max(Math.min(inputRef.current.scrollHeight, 120), 48)}px`
    }
  }, [input])

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.interimResults = false;
      
      recognitionRef.current.onstart = () => setIsRecording(true);
      recognitionRef.current.onend = () => setIsRecording(false);
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        const currentInput = inputRef.current ? inputRef.current.value : '';
        const newVal = (currentInput.trim() ? currentInput.trim() + ' ' : '') + transcript;
        setInput(newVal);
        
        clearTimeout(debounceRef.current)
        if (newVal.trim().length >= 3) {
           setStatus('idle', 'thinking suggestions…')
           debounceRef.current = setTimeout(() => fetchCompletions(newVal.trim(), messagesRef.current), 400);
        } else {
           setCompletions([])
           setStatus('idle', 'ready')
        }
      };
    }
  }, []);

  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const [selectedModel, setSelectedModel] = useState('qwen2.5:3b')
  const MODELS = ['qwen2.5:3b', 'qwen2.5:7b', 'qwen2.5:14b', 'llama3.1:8b']

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Handle outside clicks
  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false)
      if (scenarioRef.current && !scenarioRef.current.contains(e.target)) setScenarioOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (chatLogRef.current) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight
    }
  }, [messages])

  const setStatus = (type, text) => setStatusState({ type, text })

  const getContextPrompt = (sc = selectedScenario, custom = customScenarioText) => {
    if (sc.id === 'casual') return 'You are a helpful and friendly language partner.'
    if (sc.id === 'custom') return `You are a helpful language partner roleplaying a specific scenario: "${custom || 'Everyday life'}".`
    return `You are a helpful language partner roleplaying a specific scenario: "${sc.label}".`
  }

  const restartWithScenario = (sc, customText = customScenarioText) => {
    setMessages([])
    setInput('')
    setCompletions([])
    fetchGreeting(selectedLang, sc, customText)
  }

  useEffect(() => {
    restartWithScenario(selectedScenario)
  }, [selectedLang])

  const selectScenario = (sc) => {
    setSelectedScenario(sc)
    setScenarioOpen(false)
    if (sc.id !== 'custom') {
      restartWithScenario(sc, '')
    }
  }

  const fetchGreeting = async (lang, sc = selectedScenario, custom = customScenarioText) => {
    if (chatControllerRef.current) chatControllerRef.current.abort()
    chatControllerRef.current = new AbortController()

    setLoadingChat(true)
    setStatus('active', 'generating greeting…')

    try {
      const res = await fetch(`${serverUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_OLLAMA_KEY
        },
        signal: chatControllerRef.current.signal,
        body: JSON.stringify({
          model: selectedModel,
          messages: [
            {
              role: 'system',
              content: `${getContextPrompt(sc, custom)} Greet the user naturally in ${lang.label} to start a conversation. Only provide the greeting, no extra text. Keep it brief.`
            }
          ],
          stream: false,
          options: { temperature: 0.7 }
        })
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      const text = data.message?.content || ''
      setMessages([{ role: 'assistant', content: text }])
      setStatus('idle', 'ready')
      if (autoSpeakRef.current && text) {
        speak(text, voices[lang.voice], voiceSpeed)
      }
    } catch (err) {
      if (err.name === 'AbortError') return
      setStatus('error', `${err.message} — error generating greeting`)
    }
    setLoadingChat(false)
  }

  const fetchChatResponse = async (userText, currentMessages) => {
    if (chatControllerRef.current) chatControllerRef.current.abort()
    chatControllerRef.current = new AbortController()

    setLoadingChat(true)
    setStatus('active', 'generating reply…')

    const newMessages = [...currentMessages, { role: 'user', content: userText }]
    setMessages(newMessages)
    setInput('')
    setCompletions([])

    const apiMessages = [
      {
        role: 'system',
        content: `${getContextPrompt()} Chat with the user in ${selectedLang.label}. Keep responses natural, short (1-3 sentences maximum), and encourage them to reply. Do not use English unless asked.`
      },
      ...newMessages
    ]

    try {
      const res = await fetch(`${serverUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_OLLAMA_KEY
        },
        signal: chatControllerRef.current.signal,
        body: JSON.stringify({
          model: selectedModel,
          messages: apiMessages,
          stream: false,
          options: { temperature: 0.7 }
        })
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      const replyText = data.message?.content || ''
      setMessages((prev) => [...prev, { role: 'assistant', content: replyText }])
      setStatus('idle', 'ready')
      if (autoSpeakRef.current && replyText) {
        speak(replyText, voices[selectedLang.voice], voiceSpeed)
      }
    } catch (err) {
      if (err.name === 'AbortError') return
      setStatus('error', `${err.message} — error generating reply`)
    }
    setLoadingChat(false)
  }

  const handleInput = (e) => {
    const val = e.target.value
    setInput(val)
    clearTimeout(debounceRef.current)

    if (val.trim().length < 3) {
      setCompletions([])
      if (!loadingChat) setStatus('idle', 'ready')
      return
    }

    setStatus('idle', 'thinking suggestions…')
    debounceRef.current = setTimeout(() => fetchCompletions(val.trim(), messages), 400)
  }

  const fetchCompletions = async (sentence, currentMessages) => {
    if (autocompleteControllerRef.current) autocompleteControllerRef.current.abort()
    autocompleteControllerRef.current = new AbortController()

    setLoadingCompletions(true)
    const t0 = performance.now()

    const contextStr = currentMessages.length > 0
      ? currentMessages.map(m => `${m.role === 'assistant' ? 'Partner' : 'Me'}: ${m.content}`).join('\n')
      : 'No previous messages.'

    const prompt = `You are an autocomplete engine assisting the user in writing.
We are roleplaying the scenario: ${getContextPrompt()}
The conversation so far in ${selectedLang.label}:
${contextStr}

The user is currently typing the beginning of their next reply: "${sentence}"

Complete it in 3 different short ways (max 5-8 words each). Be natural, varied, and contextually appropriate for the conversation. Respond in ${selectedLang.label}.

Respond ONLY with valid JSON in exactly this format, nothing else:
{"completions": ["completion one", "completion two", "completion three"]}`

    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_OLLAMA_KEY
        },
        signal: autocompleteControllerRef.current.signal,
        body: JSON.stringify({
          model: selectedModel,
          prompt,
          stream: false,
          format: 'json',
          options: { temperature: 0.8, num_predict: 80 }
        })
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      const ms = Math.round(performance.now() - t0)
      const raw = data.response.replace(/```json|```/g, '').trim()
      const parsed = JSON.parse(raw)
      const results = parsed.completions || []

      setCompletions(results)
      setStatus('idle', `Autocomplete: ${ms}ms`)
    } catch (err) {
      if (err.name === 'AbortError') return
      setCompletions([])
    }

    setLoadingCompletions(false)
  }

  const applyCompletion = (idx) => {
    const completion = completions[idx]
    if (!completion) return
    const newVal = `${input.trim()} ${completion}`
    setInput(newVal)
    inputRef.current?.focus()
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchCompletions(newVal.trim(), messages), 400)
  }

  const handleSpeak = useCallback(
    (text) => {
      speak(text, voices[selectedLang.voice], voiceSpeed)
    },
    [selectedLang, voiceSpeed]
  )

  const handleToggleRecord = () => {
    if (!recognitionRef.current) {
        setStatus('error', 'Speech recognition not supported')
        return;
    }
    
    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      const LANG_MAP = {
        eng: 'en-US', rus: 'ru-RU', ita: 'it-IT', por: 'pt-BR', tur: 'tr-TR',
        spa: 'es-ES', swe: 'sv-SE', nld: 'nl-NL', cmn: 'zh-CN', vie: 'vi-VN',
        kor: 'ko-KR', ara: 'ar-SA', jpn: 'ja-JP', ell: 'el-GR', deu: 'de-DE',
        fra: 'fr-FR'
      }
      recognitionRef.current.lang = LANG_MAP[selectedLang.voice] || 'en-US';
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.error(e);
      }
    }
  }

  const unlockSpeech = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.resume();
      const utterance = new SpeechSynthesisUtterance('');
      utterance.volume = 0;
      window.speechSynthesis.speak(utterance);
    }
  }

  const handleSendMessage = () => {
    if (input.trim() === '') return
    
    if (autoSpeak) {
      unlockSpeech();
    }
    
    fetchChatResponse(input.trim(), messages)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setLangOpen(false)
      return
    }
    if (langOpen) return

    if (e.key === 'Tab' && completions.length > 0) {
      e.preventDefault()
      applyCompletion(0)
      return
    }

    if (['1', '2', '3'].includes(e.key) && completions.length > 0) {
      e.preventDefault()
      applyCompletion(parseInt(e.key) - 1)
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const selectLang = (lang) => {
    setSelectedLang(lang)
    setLangOpen(false)
    inputRef.current?.focus()
  }

  return (
    <div className={styles.translationFullView}>
      <div className={styles.gridBg} aria-hidden="true" />

      <div className={styles.container}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div>
              <div className={styles.label}>Mac Studio AI</div>
              <h1 className={styles.title}>
                Language <span>Partner</span>
              </h1>
            </div>

            <div className={styles.selectorsGroup}>
              {/* Scenario selector */}
              <div className={styles.langSelector} ref={scenarioRef}>
                <button className={`${styles.langBtn} ${scenarioOpen ? styles.langBtnOpen : ''}`} onClick={() => setScenarioOpen((o) => !o)}>
                  <span className={styles.langFlag}>{selectedScenario.icon}</span>
                  <span className={styles.langLabel}>{selectedScenario.label}</span>
                  <span className={`${styles.langChevron} ${scenarioOpen ? styles.langChevronOpen : ''}`}>▾</span>
                </button>

                {scenarioOpen && (
                  <div className={styles.langDropdown}>
                    {SCENARIOS.map((sc) => (
                      <button
                        key={sc.id}
                        className={`${styles.langOption} ${sc.id === selectedScenario.id ? styles.langOptionActive : ''}`}
                        onClick={() => selectScenario(sc)}>
                        <span className={styles.langFlag}>{sc.icon}</span>
                        <span>{sc.label}</span>
                        {sc.id === selectedScenario.id && <span className={styles.langCheck}>✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Language selector */}
              <div className={styles.langSelector} ref={langRef}>
              <button className={`${styles.langBtn} ${langOpen ? styles.langBtnOpen : ''}`} onClick={() => setLangOpen((o) => !o)}>
                <span className={styles.langFlag}>{selectedLang.flag}</span>
                <span className={styles.langLabel}>{selectedLang.label}</span>
                <span className={`${styles.langChevron} ${langOpen ? styles.langChevronOpen : ''}`}>▾</span>
              </button>

              {langOpen && (
                <div className={styles.langDropdown}>
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.voice}
                      className={`${styles.langOption} ${lang.voice === selectedLang.voice ? styles.langOptionActive : ''}`}
                      onClick={() => selectLang(lang)}>
                      <span className={styles.langFlag}>{lang.flag}</span>
                      <span>{lang.label}</span>
                      {lang.voice === selectedLang.voice && <span className={styles.langCheck}>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            </div>
          </div>
          
          {selectedScenario.id === 'custom' && (
            <div className={styles.customScenarioWrap}>
              <input 
                type="text" 
                placeholder="Describe your custom roleplay scenario..." 
                className={styles.customInput}
                value={customScenarioText}
                onChange={(e) => setCustomScenarioText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customScenarioText.trim() !== '') {
                    restartWithScenario(selectedScenario, customScenarioText.trim())
                    inputRef.current?.focus()
                  }
                }}
              />
              <button 
                className={styles.startCustomBtn}
                onClick={() => {
                  if (customScenarioText.trim() !== '') {
                    restartWithScenario(selectedScenario, customScenarioText.trim())
                    inputRef.current?.focus()
                  }
                }}
              >Start</button>
            </div>
          )}
        </header>

        {/* Chat Log */}
        <div className={styles.chatLog} ref={chatLogRef}>
          {messages.map((m, idx) => (
            <div key={idx} className={`${styles.messageWrap} ${styles[m.role]}`}>
              <div className={styles.messageBubble}>
                {m.content.split(' ').map((word, wIdx, arr) => (
                  <React.Fragment key={wIdx}>
                    <TranslatableWord 
                      word={word}
                      fullMessage={m.content}
                      serverUrl={serverUrl} 
                      sourceLang={selectedLang} 
                      apiKey={process.env.REACT_APP_OLLAMA_KEY} 
                    />
                    {wIdx < arr.length - 1 && ' '}
                  </React.Fragment>
                ))}
              </div>
              <button
                className={styles.messageSpeakBtn}
                onClick={(e) => {
                  e.stopPropagation()
                  handleSpeak(m.content)
                }}
                title="Speak">
                ▶ Speak
              </button>
            </div>
          ))}
          {loadingChat && (
            <div className={`${styles.messageWrap} ${styles.assistant}`}>
              <div className={styles.messageBubble}>
                <div className={styles.typingIndicator}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className={`${styles.inputWrap} ${(loadingCompletions || loadingChat) ? styles.loading : ''}`}>
          <textarea
            ref={inputRef}
            className={styles.textarea}
            rows={2}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={`Reply to your partner in ${selectedLang.label}…`}
            autoComplete="off"
            spellCheck="false"
          />
          <div className={styles.inputActions}>
            <button 
              className={`${styles.micBtn} ${isRecording ? styles.recording : ''}`} 
              onClick={handleToggleRecord} 
              title="Toggle Dictation" 
              disabled={loadingChat}
            >
              {isRecording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </button>
            <button className={styles.sendBtn} onClick={handleSendMessage} disabled={!input.trim() || loadingChat}>
              Send ↵
            </button>
          </div>
        </div>

        {/* Status bar */}
        <div className={`${styles.statusBar} ${styles[status.type] || ''}`}>
          <div className={styles.statusDot} />
          <span>{status.text}</span>
        </div>

        {/* Completions */}
        <div className={styles.responseArea}>
          {loadingCompletions && (
            <div className={styles.thinking}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          )}
          {!loadingCompletions &&
            completions.length > 0 &&
            completions.map((c, i) => (
              <div key={i} className={styles.chip} onClick={() => applyCompletion(i)}>
                <span className={styles.chipNum}>{i + 1}</span>
                <span className={styles.chipPrefix}>{input.length > 30 ? '…' + input.slice(-27) : input}</span>
                <span className={styles.chipText}>{c}</span>
                <span className={styles.chipActions}>
                  <button
                    className={styles.speakBtn}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSpeak(`${input} ${c}`)
                    }}
                    title="Speak">
                    ▶
                  </button>
                  <span className={styles.chipArrow}>Tab</span>
                </span>
              </div>
            ))}
          {!loadingCompletions && completions.length === 0 && <div className={styles.emptyState}>completions will appear here</div>}
        </div>

        {/* Hint */}
        <div className={styles.hint}>
          <span>
            <kbd className={styles.kbd}>Tab</kbd> select first
          </span>
          <span>
            <kbd className={styles.kbd}>1</kbd>
            <kbd className={styles.kbd}>2</kbd>
            <kbd className={styles.kbd}>3</kbd> select by number
          </span>
          <span>
            <kbd className={styles.kbd}>↵</kbd> Send Message
          </span>
        </div>

        {/* Config */}
        <div className={styles.config}>
          <div className={styles.configItem}>
            <span>auto-speak</span>
            <button 
              className={`${styles.toggleBtn} ${autoSpeak ? styles.toggleOn : ''}`} 
              onClick={() => {
                const nextState = !autoSpeak;
                setAutoSpeak(nextState);
                if (nextState) unlockSpeech();
              }}>
              {autoSpeak ? 'ON' : 'OFF'}
            </button>
          </div>
          <div className={styles.configItem}>
            <span>model</span>
            <select
              className={styles.modelSelect}
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className={styles.configItem}>
            <span>endpoint</span>
            <span className={styles.configValue}>Mac Studio</span>
          </div>
          <div className={styles.configItem}>
            <span>voice</span>
            <span className={styles.configValue}>{selectedLang.voice}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
